/**
 * Rotar contraseña (F3b): la actual pasa al historial con snapshot de accesos.
 * Exige el Vault desbloqueado.
 */
import { useEffect, useState } from 'react';
import { Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { errMsg, isVaultLockedError, withUnlockRetry } from '@/hooks/useVault';

function generatePassword(length = 20): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*-_+=?';
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

interface RotateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  secretId: string;
  version: number;
  ensureUnlocked: () => Promise<boolean>;
}

export function RotateDialog({ open, onOpenChange, secretId, version, ensureUnlocked }: RotateDialogProps) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [ack, setAck] = useState(false);
  useEffect(() => {
    if (open) { setPassword(''); setShow(false); setAck(false); }
  }, [open]);

  const rotate = trpc.vault.rotateSecret.useMutation({
    onSuccess: () => {
      utils.vault.getSecret.invalidate({ secretId });
      utils.vault.listSecrets.invalidate();
      utils.vault.listSecretVersions.invalidate({ secretId });
      toast.success(t('vault.rotate.done', 'Contraseña rotada · la anterior quedó en el historial'));
      onOpenChange(false);
    },
    onError: (e) => { if (!isVaultLockedError(e)) toast.error(errMsg(e)); },
  });

  const submit = async () => {
    // los toasts salen de onSuccess/onError del mutation; acá solo el reintento por VAULT_LOCKED
    await withUnlockRetry(ensureUnlocked, () => rotate.mutateAsync({ secretId, version, newPassword: password })).catch(() => undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('vault.rotate.title', 'Rotar contraseña')}</DialogTitle>
          <DialogDescription>
            {t('vault.rotate.body', 'Cambiala primero en la plataforma y después guardala acá. La anterior queda en el historial, visible solo para quien tenía acceso en ese momento.')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="rot-pass">{t('vault.rotate.new_password', 'Nueva contraseña')}</Label>
            <div className="flex gap-2">
              <Input id="rot-pass" type={show ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="font-mono" maxLength={1024} autoFocus />
              <Button type="button" variant="outline" onClick={() => { setPassword(generatePassword()); setShow(true); }} title={t('vault.form.generate', 'Generar')} aria-label={t('vault.form.generate', 'Generar')} data-tour="rotate-generate">
                <Wand2 className="h-4 w-4" />
              </Button>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Checkbox checked={show} onCheckedChange={(v) => setShow(v === true)} />
              {t('vault.form.show_password', 'Mostrar')}
            </label>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={ack} onCheckedChange={(v) => setAck(v === true)} className="mt-0.5" data-tour="rotate-ack" />
            {t('vault.rotate.ack', 'Ya la cambié en la plataforma')}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel', 'Cancelar')}</Button>
          <Button onClick={submit} disabled={!password || !ack || rotate.isPending} data-tour="rotate-submit">
            {rotate.isPending ? t('vault.form.saving', 'Guardando…') : t('vault.rotate.cta', 'Rotar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
